<template>
  <div class="textarea-container" :class="{ 'details-compensate': hideDetails === 2 }">
    <slot name="label">
      <ui-label
        v-if="label"
        :text="label"
        :help-tooltip="labelHelp"
      />
    </slot>
    <textarea
      class="textarea"
      :class="{ 'error-area': hasError, disabled }"
      :rows="rows"
      :value="textareaValue"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      @blur="emit('blur', $event)"
      @input="onInput"
      @change="onChange"
      @focus="emit('focus', $event)"
    />
    <div
      v-if="hideDetails === 0 && hasError"
      class="error-message"
      v-html="errorMsg"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    value?: string | number | null;
    modelValue?: string | number | null;
    label?: string;
    labelHelp?: string;
    hideDetails?: number;
    readonly?: boolean;
    disabled?: boolean;
    rows?: number;
    placeholder?: string;
    errorMessages?: string | string[] | null;
  }>(),
  {
    label: '',
    hideDetails: 0,
    readonly: false,
    disabled: false,
    rows: 5,
    placeholder: '',
    errorMessages: () => [],
  },
);

const emit = defineEmits<{
  input: [value: string];
  'update:modelValue': [value: string];
  blur: [event: FocusEvent];
  change: [value: string];
  focus: [event: FocusEvent];
}>();

const textareaValue = computed(() => {
  const value = props.modelValue !== undefined && props.modelValue !== null
    ? props.modelValue
    : props.value;
  return value === undefined || value === null ? '' : String(value);
});

const hasError = computed(() => {
  if (Array.isArray(props.errorMessages)) {
    return props.errorMessages.length > 0;
  }
  return Boolean(props.errorMessages);
});

const errorMsg = computed(() => {
  if (Array.isArray(props.errorMessages)) {
    if (props.errorMessages.length) {
      return props.errorMessages.join('<br>');
    }
    return '';
  }
  return props.errorMessages;
});

const onInput = (event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value;
  emit('input', value);
  emit('update:modelValue', value);
};

const onChange = (event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value;
  emit('change', value);
};
</script>

<style lang="scss" scoped>
.textarea-container {
  display: flex;
  flex-direction: column;

  :deep(label) {
    font-size: 13px;
    font-weight: bold;
  }

  &.details-compensate {
    margin-bottom: 12px;
  }
}

.textarea {
  width: 100%;
  border: 1px solid var(--color-gray20);
  border-radius: 6px;
  padding: 10px;
  background-color: var(--color-white);

  &::placeholder {
    color: var(--color-gray40) !important;
  }

  &.error-area {
    border-color: var(--color-red);
    caret-color: initial;
  }

  &.disabled {
    border-color: var(--color-gray10);
  }

  &:focus {
    outline: none;
  }
}

.error-message {
  font-style: italic;
  font-size: 12px;
  line-height: 12px;
  color: var(--color-red) !important;
}
</style>
